import useLocalStorage from 'core/hooks/useLocalStorage'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import APIService from 'api/APIService'
import { downloadFile } from 'utils/AppHelper'
import { ID, JobStatus } from 'types/entity'
import Button from 'core/components/buttons/Button'
import useCurrentTeam from 'core/hooks/useCurrentTeam'
import { useSettings } from 'core/hooks/useSettings'
import useNotifications from 'core/hooks/useNotifications'

interface BulkUpdateProps {
  onSuccess?: VoidFunction
  teamId?: ID
  isInvite?: boolean
}

const useBulkMemberUpdate = ({
  onSuccess,
  teamId,
  isInvite = false
}: BulkUpdateProps = {}) => {
  const queryClient = useQueryClient()
  const { data: currentTeam } = useCurrentTeam()
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const [localJobId, setJobId] = useLocalStorage('member_update_job_id', '')
  const jobID = settings.pollingJobs.memberBulkUpdateJobId || localJobId

  const { mutate: downloadCSVTemplate, status: templateStatus } = useMutation(
    () => {
      return APIService.members.downloadBulkUpdateCSVTemplate({
        teamId: teamId as ID
      })
    },
    {
      onSuccess: (data) => {
        const csvContent = `data:text/csv;charset=utf-8,${data}`
        const encodedURI = encodeURI(csvContent)
        downloadFile(encodedURI, 'update-member-template')
      },
      onError: () => {
        notification.error('An error has occurred trying to download this file')
      }
    }
  )

  useQuery(
    ['member-export-polling', jobID],
    () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    {
      enabled: !!jobID,
      refetchInterval: 2000,
      onSuccess: (response: JobStatus) => {
        if (response.status === 'finished' && !response.data?.errors) {
          notification.dismiss('memberBulkUpdateJobId')
          setJobId('')
          set('pollingJobs', {
            ...settings.pollingJobs,
            memberBulkUpdateJobId: ''
          })
          queryClient.invalidateQueries({
            queryKey: ['members', currentTeam?.token_id]
          })

          notification.success({
            title: response.data.message as string,
            id: jobID
          })
        }

        if (
          response.status === 'failed' ||
          (response.status === 'finished' && response.data?.errors)
        ) {
          notification.dismiss('memberBulkUpdateJobId')
          notification.error({
            title: `We couldn't ${isInvite ? 'invite' : 'upload'} all members`,
            subtitle: (
              <div className="mt-2">
                <p>How to fix the errors</p>
                <p className="ml-2">1. Download and open the errors file</p>
                <p className="ml-2">2. Fix the errors and re-upload the file</p>
                <p className="my-3">
                  Need more help?{' '}
                  <Button
                    variant="link"
                    className="underline"
                    onClick={() => {
                      window.open(
                        'https://www.everlance.com/contact-us',
                        '_blank'
                      )
                    }}
                  >
                    Contact support
                  </Button>
                </p>
              </div>
            ),
            id: jobID,
            className: 'w-[26rem] max-w-full',
            footer: () => {
              return (
                <div className="flex gap-3">
                  <>
                    {response.data.failed_messages_upload_url && (
                      <Button
                        className="text-primary"
                        variant="link"
                        onClick={() => {
                          downloadFile(
                            response.data.failed_messages_upload_url as string,
                            'errors_list'
                          )
                        }}
                      >
                        Download the errors file
                      </Button>
                    )}

                    {response.data.failed_rows_upload_url && (
                      <Button
                        className="text-primary"
                        variant="link"
                        onClick={() => {
                          downloadFile(
                            response.data.failed_rows_upload_url as string,
                            'errors_rows_list'
                          )
                        }}
                      >
                        Download the failed rows
                      </Button>
                    )}
                  </>
                </div>
              )
            }
          })
          setJobId('')
          set('pollingJobs', {
            ...settings.pollingJobs,
            memberBulkUpdateJobId: ''
          })
        }
      },
      onError: () => {
        notification.dismiss('memberBulkUpdateJobId')
        notification.error({
          title: `Could not ${
            isInvite ? 'invite' : 'update'
          } members at this time`,
          id: jobID
        })
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBulkUpdateJobId: ''
        })
      }
    }
  )

  const uploadQuery = useMutation(
    ['upload', 'update', 'csv'],
    ({ csv, teamId }: { csv: File; teamId: ID }) => {
      return APIService.members.uploadCsvBulkMemberUpdate({
        file: csv as File,
        teamId
      })
    },
    {
      onSuccess: (data) => {
        notification.success({
          title: 'Upload successful',
          duration: 3000,
          id: 'bulkUpdateUpload'
        })

        notification.loading({
          title: `${isInvite ? 'Inviting' : 'Updating'} members...`,
          subtitle:
            'This may take a few minutes. You can close this pop up and we’ll keep working in the background.',
          duration: Infinity,
          id: 'memberBulkUpdateJobId'
        })
        setTimeout(() => {
          set('pollingJobs', {
            ...settings.pollingJobs,
            memberBulkUpdateJobId: data.job_token_id
          })
        }, 0)
        setJobId(data.job_token_id)
        onSuccess?.()
      },
      onError: () => {
        notification.error({
          title: 'An error has occurred trying to upload your file',
          id: 'bulkUpdateUpload'
        })
      }
    }
  )

  return {
    downloadCSVTemplate,
    uploadQuery,
    templateStatus
  }
}

export default useBulkMemberUpdate
